import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import Button from "@rio-cloud/rio-uikit/Button";
import Dialog from "@rio-cloud/rio-uikit/Dialog";

import { Scheduling } from "../../../../models/schedule";
import { Checkup } from "../../../../models/checkup";
import { fetchCheckupByMetricAndModel } from "../../../../api/fetchCheckup";

interface ICheckupDetailDialogProps {
    scheduling?: Scheduling;
    showCheckupDetailDialog: boolean;
    setShowCheckupDetailDialog: (show: boolean) => void;
}

const CheckupDetailDialog = ({ scheduling, showCheckupDetailDialog, setShowCheckupDetailDialog }: ICheckupDetailDialogProps) => {
    const intl = useIntl();
    const [checkupDetails, setCheckupDetails] = useState<Checkup | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (showCheckupDetailDialog && scheduling) {
            fetchCheckupDetails();
        }
    }, [showCheckupDetailDialog, scheduling]);

    const fetchCheckupDetails = async () => {

        
        if (!scheduling?.asset?.chassis || !scheduling?.asset?.group) return;
        
        setLoading(true);
        try {
            const checkup = await fetchCheckupByMetricAndModel(
                scheduling.checkup?.odometer || 0,
                scheduling.checkup?.hourmeter || 0,
                scheduling.asset.chassis,
                scheduling.asset.group
            );
            
            if (checkup) {
                setCheckupDetails(checkup);
            }
        } catch (error) {
            console.error('Erro ao buscar detalhes do checkup:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!scheduling) return null;

    // Usar dados do checkupDetails se disponível, senão usar dados do scheduling
    const effectiveCheckup = checkupDetails || scheduling.checkup;

    const hasServices = effectiveCheckup?.services && effectiveCheckup.services.length > 0;
    const hasParts = effectiveCheckup?.parts && effectiveCheckup.parts.length > 0;
    const hasAdditionalServices = effectiveCheckup?.aditionalServices && effectiveCheckup.aditionalServices.length > 0;

    return (
        <Dialog
            show={showCheckupDetailDialog}
            title={intl.formatMessage({ id: "intl-msg:monitoring.checkup.title" })}
            subtitle={intl.formatMessage({ id: "intl-msg:monitoring.checkup.subtitle" })}
            footer={
                <Button
                    onClick={() => setShowCheckupDetailDialog(false)}
                    className="btn btn-primary"
                    bsSize="lg"
                >
                    {intl.formatMessage({ id: "intl-msg:monitoring.checkup.closeButton" })}
                </Button>
            }
            body={
                <div className="w-full mx-auto bg-white rounded-lg p-6">
                    {/* Revisão - KM e Data */}
                    <section className="margin-bottom-20">
                        <h2 className="text-size-16 font-semibold mb-2 text-gray-700">
                            {intl.formatMessage({ id: "intl-msg:monitoring.checkup.reviewSection" })}
                        </h2>
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex justify-between items-center">
                            <span className="text-size-14 text-gray-700">
                                {effectiveCheckup?.odometer ? `${effectiveCheckup.odometer.toLocaleString('pt-BR')} Km` : '-'}
                            </span>
                            <span className="text-size-14 text-gray-700">
                                {scheduling?.date ? scheduling.date.toLocaleDateString('pt-BR') : '-'}
                            </span>
                        </div>
                    </section>

                    {/* Itens a serem trocados */}
                    <section className="margin-bottom-20">
                        <h2 className="text-size-16 font-semibold mb-2 text-gray-700">
                            {intl.formatMessage({ id: "intl-msg:monitoring.checkup.itemsSection" })}
                        </h2>
                        {loading ? (
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-size-14 text-gray-500 text-center">
                                    {intl.formatMessage({ id: "intl-msg:monitoring.checkup.loadingInfo" })}
                                </p>
                            </div>
                        ) : hasParts ? (
                            <div className="space-y-2">
                                {effectiveCheckup.parts?.map((part: any, index: number) => (
                                    <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                        <p className="text-size-14 text-gray-700">
                                            {part.qtd}x {part.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : hasServices ? (
                            <div className="space-y-2">
                                {effectiveCheckup.services?.map((service: any, index: number) => (
                                    <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                        <p className="text-size-14 text-gray-700 font-medium mb-2">
                                            {service.name}
                                        </p>
                                        {service.items && service.items.length > 0 && (
                                            <div className="space-y-1">
                                                {service.items.map((item: any, itemIndex: number) => (
                                                    <p key={itemIndex} className="text-size-14 text-gray-600 padding-left-20">
                                                        {item}
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-size-14 text-gray-500 text-center">
                                    {intl.formatMessage({ id: "intl-msg:monitoring.checkup.noItems" })}
                                </p>
                            </div>
                        )}
                    </section>

                    {/* Serviços Complementares */}
                    {hasAdditionalServices && (
                        <section className="margin-bottom-20">
                            <h2 className="text-size-16 font-semibold mb-2 text-gray-700">
                                {intl.formatMessage({ id: "intl-msg:monitoring.checkup.additionalServicesSection" })}
                            </h2>
                            <div className="space-y-2">
                                {effectiveCheckup.aditionalServices?.map((service: any, index: number) => (
                                    <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                        <p className="text-size-14 text-gray-700">
                                            {service.name}
                                        </p>
                                        {service.price && service.price > 0 && (
                                            <p className="text-size-12 text-gray-500 mt-1">
                                                {intl.formatMessage({ id: "intl-msg:monitoring.checkup.priceLabel" })}: R$ {service.price.toFixed(2)}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            }
            onCloseValidation={() => {
                setShowCheckupDetailDialog(false)
                return true;
            }}
            className="dialog-agendamento"
            showCloseButton
        />
    );
}

export default CheckupDetailDialog;